import { combineReducers } from 'redux';

import {
  GET_AVG_POPULAR_TIME_FULFILLED,
  GET_AVG_POPULAR_TIME_PENDING,
  GET_AVG_POPULAR_TIME_REJECTED,
  GET_DCARD_DB_STATS_FULFILLED,
  GET_DCARD_DB_STATS_PENDING,
  GET_DCARD_DB_STATS_REJECTED,
  GET_DCARD_HOT_POSTS_FULFILLED,
  GET_DCARD_HOT_POSTS_PENDING,
  GET_DCARD_HOT_POSTS_REJECTED,
  GET_HOT_ARTICLES_FULFILLED,
  GET_HOT_ARTICLES_PENDING,
  GET_HOT_ARTICLES_REJECTED,
  GET_PTT_DB_STATS_FULFILLED,
  GET_PTT_DB_STATS_PENDING,
  GET_PTT_DB_STATS_REJECTED,
  GET_USER_GEO_DISTRIBUTIONS_FULFILLED,
  GET_USER_GEO_DISTRIBUTIONS_PENDING,
  GET_USER_GEO_DISTRIBUTIONS_REJECTED,
} from '../types';

// State interfaces
interface AvgPopularTimeState {
  data: any;
  isFetching: boolean;
  error: any | null;
}

interface HotArticlesState {
  data: any[];
  sinceDaysAgo: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface DbStatsState {
  data: any;
  isFetching: boolean;
  error: any | null;
}

interface UserGeoDistributionsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface HotPostsState {
  data: any[];
  sinceDaysAgo: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface PttState {
  avgPopularTime: AvgPopularTimeState;
  hotArticles: HotArticlesState;
  userGeoDistributions: UserGeoDistributionsState;
  dbStats: DbStatsState;
}

interface DcardState {
  dbStats: DbStatsState;
  hotPosts: HotPostsState;
}

interface MetricState {
  ptt: PttState;
  dcard: DcardState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_AVG_POPULAR_TIME_PENDING | typeof GET_HOT_ARTICLES_PENDING |
        typeof GET_USER_GEO_DISTRIBUTIONS_PENDING | typeof GET_PTT_DB_STATS_PENDING |
        typeof GET_DCARD_DB_STATS_PENDING | typeof GET_DCARD_HOT_POSTS_PENDING;
}

interface RejectedAction {
  type: typeof GET_AVG_POPULAR_TIME_REJECTED | typeof GET_HOT_ARTICLES_REJECTED |
        typeof GET_USER_GEO_DISTRIBUTIONS_REJECTED | typeof GET_PTT_DB_STATS_REJECTED |
        typeof GET_DCARD_DB_STATS_REJECTED | typeof GET_DCARD_HOT_POSTS_REJECTED;
  error: any;
}

interface AvgPopularTimeFulfilledAction {
  type: typeof GET_AVG_POPULAR_TIME_FULFILLED;
  payload: {
    data: any;
  };
}

interface HotArticlesFulfilledAction {
  type: typeof GET_HOT_ARTICLES_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    sinceDaysAgo: number;
    offset: number;
    limit: number;
  };
}

interface UserGeoDistributionsFulfilledAction {
  type: typeof GET_USER_GEO_DISTRIBUTIONS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface DbStatsFulfilledAction {
  type: typeof GET_PTT_DB_STATS_FULFILLED | typeof GET_DCARD_DB_STATS_FULFILLED;
  payload: {
    data: any;
  };
}

interface HotPostsFulfilledAction {
  type: typeof GET_DCARD_HOT_POSTS_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    sinceDaysAgo: number;
    offset: number;
    limit: number;
  };
}

type AvgPopularTimeAction = PendingAction | AvgPopularTimeFulfilledAction | RejectedAction;
type HotArticlesAction = PendingAction | HotArticlesFulfilledAction | RejectedAction;
type UserGeoDistributionsAction = PendingAction | UserGeoDistributionsFulfilledAction | RejectedAction;
type DbStatsAction = PendingAction | DbStatsFulfilledAction | RejectedAction;
type HotPostsAction = PendingAction | HotPostsFulfilledAction | RejectedAction;

const INITIAL_STATE = {
  // PTT
  AVG_POPULAR_TIME: {
    data: {},
    isFetching: false,
    error: null,
  },
  HOT_ARTICLES: {
    data: [],
    sinceDaysAgo: 1,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  PTT_DB_STATS: {
    data: {},
    isFetching: false,
    error: null,
  },
  USER_GEO_DISTRIBUTIONS: {
    data: [],
    isFetching: false,
    error: null,
  },
  // Dcard
  DCARD_DB_STATS: {
    data: {},
    isFetching: false,
    error: null,
  },
  DCARD_HOT_POSTS: {
    data: [],
    sinceDaysAgo: 1,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
};

const avgPopularTime = (state: AvgPopularTimeState = INITIAL_STATE.AVG_POPULAR_TIME, action: AvgPopularTimeAction): AvgPopularTimeState => {
  switch (action.type) {
    case GET_AVG_POPULAR_TIME_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_AVG_POPULAR_TIME_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_AVG_POPULAR_TIME_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const hotArticles = (state: HotArticlesState = INITIAL_STATE.HOT_ARTICLES, action: HotArticlesAction): HotArticlesState => {
  switch (action.type) {
    case GET_HOT_ARTICLES_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_HOT_ARTICLES_FULFILLED: {
      const {
        sinceDaysAgo,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        data,
        sinceDaysAgo,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_HOT_ARTICLES_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const userGeoDistributions = (state: UserGeoDistributionsState = INITIAL_STATE.USER_GEO_DISTRIBUTIONS, action: UserGeoDistributionsAction): UserGeoDistributionsState => {
  switch (action.type) {
    case GET_USER_GEO_DISTRIBUTIONS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_USER_GEO_DISTRIBUTIONS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_USER_GEO_DISTRIBUTIONS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const pttDBStats = (state: DbStatsState = INITIAL_STATE.PTT_DB_STATS, action: DbStatsAction): DbStatsState => {
  switch (action.type) {
    case GET_PTT_DB_STATS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_PTT_DB_STATS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_PTT_DB_STATS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const dcardDBStats = (state: DbStatsState = INITIAL_STATE.DCARD_DB_STATS, action: DbStatsAction): DbStatsState => {
  switch (action.type) {
    case GET_DCARD_DB_STATS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_DCARD_DB_STATS_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_DCARD_DB_STATS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const dcardHotPosts = (state: HotPostsState = INITIAL_STATE.DCARD_HOT_POSTS, action: HotPostsAction): HotPostsState => {
  switch (action.type) {
    case GET_DCARD_HOT_POSTS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_DCARD_HOT_POSTS_FULFILLED: {
      const {
        sinceDaysAgo,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        data,
        sinceDaysAgo,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_DCARD_HOT_POSTS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<MetricState>({
  ptt: combineReducers<PttState>({
    avgPopularTime,
    hotArticles,
    userGeoDistributions,
    dbStats: pttDBStats,
  }),
  dcard: combineReducers<DcardState>({
    dbStats: dcardDBStats,
    hotPosts: dcardHotPosts,
  }),
});